/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { GuardedFeature } from '@recruitee/feature-guard';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { InterviewPreviewService } from '@recruitee/interview-templates';
import {
  InterviewTemplateScope,
  InterviewTemplateSelectors,
} from '@recruitee/interview-templates-data';
import type { InterviewTemplate } from '@recruitee/interview-templates-types';
import { WorkflowTemplatesAnalytics } from '@recruitee/temp';
import {
  DialogRef,
  DialogService,
  DialogWidth,
  KeyCode,
  RtStatefulComponent,
  ButtonComponent,
  EmptyStateComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
} from '@recruitee/user-interface';
import { WorkflowTemplatesFeatures } from '@recruitee/workflow-templates-types';

import { RedactorInsertsDialogComponent } from '../../../wysiwyg-extensions/link/redactor-inserts-dialog/redactor-inserts-dialog.component';
import { WysiwygLink, WysiwygLinkType } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsDropdownHeaderComponent } from '../redactor-inserts-dropdown-header/redactor-inserts-dropdown-header.component';

type State = {
  questionnairesTemplates: InterviewTemplate[];
  questionnairesTemplateGroups: QuestionnairesTemplateGroup[];
  searchQuery: string;
  selectedId: number | null;
  hasFocus: boolean;
};

type DialogOpts = {
  label: string;
  link: string;
};

type QuestionnairesTemplateGroup = {
  groupName: string;
  groupedQuestionnairesTemplates: InterviewTemplate[];
};

@Component({
  selector: 'rt-redactor-inserts-questionnaires',
  templateUrl: './redactor-inserts-questionnaires.component.html',
  styleUrls: ['./redactor-inserts-questionnaires.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RedactorInsertsDropdownHeaderComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    EmptyStateComponent,
    ButtonComponent,
    NgTemplateOutlet,
    TranslatePipe,
  ],
})
export class RedactorInsertsQuestionnairesComponent extends RtStatefulComponent<State> {
  @Input() public inside: boolean;
  @Input() public insideTemplate: TemplateRef<any>;
  @Input() public hasBackAction: boolean = true;

  @Output() public back: EventEmitter<void> = new EventEmitter();
  @Output() public settings: EventEmitter<string> = new EventEmitter();
  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();
  @Output() public insertLink: EventEmitter<WysiwygLink> = new EventEmitter();

  public GuardedFeatures: typeof GuardedFeature = GuardedFeature;

  @ViewChildren('container') private containerList: QueryList<ElementRef>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private store: Store,
    private router: Router,
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    private interviewPreview: InterviewPreviewService,
    private workflowTemplatesAnalytics: WorkflowTemplatesAnalytics,
    @Optional() private dialogRef: DialogRef,
  ) {
    super(cdRef);
    this.createState({
      questionnairesTemplates: [],
      questionnairesTemplateGroups: [],
      searchQuery: '',
      selectedId: null,
      hasFocus: false,
    });
  }

  public ngOnInit(): void {
    this.store
      .select(InterviewTemplateSelectors.entities({ scope: InterviewTemplateScope.Questionnaire }))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(questionnairesTemplates => {
        const groupedTemplates = this.groupAndFilter(
          questionnairesTemplates,
          this.state.searchQuery,
        );

        this.setState({
          ...this.state,
          questionnairesTemplateGroups: groupedTemplates,
          questionnairesTemplates: questionnairesTemplates,
          selectedId: null,
        });
      });

    this.containerList.changes
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe((containerList: QueryList<ElementRef>) => {
        if (containerList.first && this.state.questionnairesTemplates.length < 6) {
          containerList.first.nativeElement.focus();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public templatesTrackByFn<T extends { id?: number }>(_index: number, template: T): number {
    return template.id!;
  }

  public groupsTrackByFn<T extends { groupName: string }>(_index: number, group: T): string {
    return group.groupName;
  }

  public goBack(): void {
    this.back.emit();
  }
  public goToSettings(url: string): void {
    this.settings.emit(url);
  }

  public onSearchQueryChange(searchQuery: string): void {
    this.setState({
      ...this.state,
      questionnairesTemplateGroups: this.groupAndFilter(
        this.state.questionnairesTemplates,
        searchQuery,
      ),
      searchQuery,
    });
  }

  public questionnairesTemplateChoose(template: InterviewTemplate): void {
    this.insertLink.emit(
      this.createQuestionnaireUrl(template, {
        label: '',
        link: '',
      }),
    );
    this.workflowTemplatesAnalytics.trackTemplateInsertion(
      WorkflowTemplatesFeatures.Questionnaires,
    );
    this.closeDropdown.emit();
  }

  public openModal(template: InterviewTemplate) {
    const dialog = this.dialogService.openCustom(
      RedactorInsertsDialogComponent,
      { width: DialogWidth.Small },
      {
        heading: this.i18n.translate('app.redactor_inserts.questionnaires.dialog.heading', {
          questionnaire_name: this.insertLink.name,
        }),
        type: WysiwygLinkType.Questionnaires,
        link: this.interviewPreview.generateLinkTemplate(template.token!),
        label: '',
        isEdit: false,
      },
      this.vcRef,
    );
    dialog.afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(opts => {
      if (opts) {
        this.insertLink.emit(this.createQuestionnaireUrl(template, opts));
      }
      this.closeDropdown.emit();
    });
  }

  public navigateToQuestionnaires(): void {
    if (this.dialogRef) {
      this.dialogRef.afterClosed.subscribe(() => {
        this.router.navigate([`/settings/questionnaires`]);
      });

      this.dialogRef.close();
    } else {
      this.router.navigate([`/settings/questionnaires`]);
    }
  }

  public onFocusChanges(hasFocus: boolean): void {
    this.setState({
      ...this.state,
      hasFocus,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    switch (key) {
      case KeyCode.Enter: {
        event.preventDefault();
        if (this.state.selectedId === null) {
          return;
        } else {
          this.questionnairesTemplateChoose(
            this.state.questionnairesTemplates.find(s => s.id === this.state.selectedId)!,
          );
        }
        break;
      }

      case KeyCode.Backspace:
        if ((!this.state.hasFocus || this.state.searchQuery.length === 0) && this.hasBackAction) {
          event.preventDefault();
          this.back.emit();
        }
        break;

      case KeyCode.UpArrow: {
        event.preventDefault();
        const flattenTemplates = this.state.questionnairesTemplateGroups.reduce<
          InterviewTemplate[]
        >((acc, gT) => acc.concat(gT.groupedQuestionnairesTemplates), []);
        const index = flattenTemplates.findIndex(t => t.id === this.state.selectedId);
        if (index <= 0) {
          this.setState({
            ...this.state,
            selectedId: flattenTemplates[flattenTemplates.length - 1].id!,
          });
        } else {
          this.setState({ ...this.state, selectedId: flattenTemplates[index - 1].id! });
        }
        break;
      }

      case KeyCode.DownArrow: {
        event.preventDefault();
        const flattenTemplates = this.state.questionnairesTemplateGroups.reduce<
          InterviewTemplate[]
        >((acc, gT) => acc.concat(gT.groupedQuestionnairesTemplates), []);
        const index = flattenTemplates.findIndex(t => t.id === this.state.selectedId);
        if (index === flattenTemplates.length - 1 || index === -1) {
          this.setState({
            ...this.state,
            selectedId: flattenTemplates[0].id!,
          });
        } else {
          this.setState({ ...this.state, selectedId: flattenTemplates[index + 1].id! });
        }
        break;
      }

      default:
        return;
    }
  }

  public isSelected(template: InterviewTemplate, { elRef }: { elRef: ElementRef }): boolean {
    const isSelected = this.state.selectedId === template.id;
    if (isSelected && elRef) {
      elRef.nativeElement.scrollIntoView();
    }
    return isSelected;
  }

  private createQuestionnaireUrl(template: InterviewTemplate, opts?: DialogOpts): WysiwygLink {
    return {
      url: opts?.link || this.interviewPreview.generateLinkTemplate(template.token!),
      label: opts?.label || this.interviewPreview.generateLinkTemplate(template.token!),
      defaultLabel: this.interviewPreview.generateLinkTemplate(template.token!),
      type: WysiwygLinkType.Questionnaires,
      name: template.name,
      id: template.token,
    };
  }

  private groupAndFilter(
    questionnairesTemplates: InterviewTemplate[],
    searchQuery: string,
  ): QuestionnairesTemplateGroup[] {
    const query = searchQuery && searchQuery.toLowerCase();
    const byGroupName = questionnairesTemplates
      .filter(
        questionnaireTemplate => questionnaireTemplate.name.toLowerCase().indexOf(query) !== -1,
      )
      .reduce<Record<string, InterviewTemplate[]>>(
        (acc, tpl) => ({
          ...acc,
          [tpl.category || 'null']: [...(acc[tpl.category!] || []), tpl],
        }),
        {},
      );
    const groupNames = Object.keys(byGroupName);
    return groupNames.map(groupName => {
      const groupedQuestionnairesTemplates = byGroupName[groupName];
      return {
        // eslint-disable-next-line max-lines
        groupName: groupName === 'null' ? this.i18n.translate('app.common.other') : groupName,
        groupedQuestionnairesTemplates,
      };
    });
  }
}
